// internationalization
import { useTranslation } from 'react-i18next';

// material ui icons
import SpeedRoundedIcon from '@mui/icons-material/SpeedRounded';

// material ui
import { Stack, Typography } from '@mui/material';

// feature-components
import CopyableIdField from '@/features/devices/components/shared/CopyableIdField';
import DetailHeaderCard from '@/features/devices/components/shared/DetailHeaderCard';
import DetailInfoField from '@/features/devices/components/shared/DetailInfoField';
import useCopyToClipboard from '@/features/devices/components/shared/hooks/useCopyToClipboard';

// relative
import { DataIntervalStatus } from './DataIntervalStatus';
import SensorStatusCell from './SensorStatusCell';

import { DateTime } from 'luxon';

const SensorDetailHeader = ({ sensor }) => {
  const { t } = useTranslation(['units-gateways-sensors', 'alarms']);
  const copyToClipboard = useCopyToClipboard();

  const handleCopySensorId = () =>
    copyToClipboard(
      sensor?.SensorId,
      t?.('sensors.sensorIdCopied', { id: sensor.SensorId }) ||
        `Sensor ID copied: ${sensor.SensorId}`,
      t?.('sensors.sensorIdCopyFailed') || 'Failed to copy Sensor ID',
    );

  return (
    <DetailHeaderCard
      title={`${sensor?.SensorType || t('common.notAvailable')} ${sensor?.CalibrationType ? `(${sensor.CalibrationType})` : ''}`}
      avatar={<SpeedRoundedIcon fontSize="medium" />}
      gap={3}
    >
      <DetailInfoField label={t('sensors.status')}>
        <Stack direction="row" sx={{ ml: -1 }} alignItems="center" spacing={1}>
          <SensorStatusCell
            sensor={sensor ? { SensorId: sensor.SensorId, ...sensor } : null}
          />
        </Stack>
      </DetailInfoField>

      <DetailInfoField label={t('sensors.sensorId')}>
        <CopyableIdField
          value={sensor?.SensorId}
          notAvailableText={t('common.notAvailable')}
          onCopy={handleCopySensorId}
        />
      </DetailInfoField>

      <DetailInfoField label={t('sensors.dataInterval')}>
        <Stack direction="row" alignItems="center" spacing={0.75}>
          <DataIntervalStatus
            sensorId={sensor?.SensorId}
            sensor={sensor}
            variant="body1"
            size="small"
            editable={true}
          />
        </Stack>
      </DetailInfoField>

      <DetailInfoField label={t('gateways.reportedFirmware')}>
        <Typography variant="body1" sx={{ fontWeight: 500 }}>
          {sensor?.CurrentApplicationFirmware || t('common.notAvailable')}
        </Typography>
      </DetailInfoField>

      <DetailInfoField label={t('sensors.lastRecorded')}>
        <Typography variant="body1" sx={{ fontWeight: 500 }}>
          {DateTime.fromISO(sensor?.DataLastRecorded).toRelative({
            base: DateTime.now(),
          }) || t('detail_page.unknown', { ns: 'alarms' })}
        </Typography>
      </DetailInfoField>
    </DetailHeaderCard>
  );
};

export default SensorDetailHeader;
